import { ref, shallowRef } from 'vue';

/**
 * "Draw a box around the bit that's wrong."
 *
 * Two clicks, snipping-tool style: the first plants a corner, the box follows
 * the pointer, the second finishes it. Deliberately click-click rather than
 * drag: a drag across a page full of links and buttons starts a text selection
 * and can end on a control that swallows pointerup, and the whole point of
 * this overlay is that the page underneath stops reacting.
 *
 * The rectangle is recorded in TWO coordinate spaces because we do not yet
 * know which one the capture will arrive in — a tab capture is the viewport, a
 * monitor capture is the screen. `mapRegionToFrame` picks between them once
 * the frame's own `displaySurface` is known.
 */
const active = ref(false);
const anchor = shallowRef(null); // first corner, viewport coords
const pointer = shallowRef(null); // live pointer, viewport coords

let resolveRegion = null;

/** Viewport point → screen point. Approximate by construction: the browser
 *  exposes no exact viewport origin, so the chrome height is inferred. */
function toScreen(x, y) {
  const chromeHeight = Math.max(0, (window.outerHeight || 0) - (window.innerHeight || 0));
  // Side borders are split evenly; on every mainstream desktop browser this
  // is 0, and a few pixels out only matters for a monitor capture.
  const sideBorder = Math.max(0, ((window.outerWidth || 0) - (window.innerWidth || 0)) / 2);
  return {
    x: (window.screenX || window.screenLeft || 0) + sideBorder + x,
    y: (window.screenY || window.screenTop || 0) + chromeHeight + y,
  };
}

function rectFrom(a, b) {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(a.x - b.x),
    height: Math.abs(a.y - b.y),
  };
}

/** The live rectangle, in viewport coords, or null before the first click. */
export function currentRect() {
  if (!anchor.value || !pointer.value) return null;
  return rectFrom(anchor.value, pointer.value);
}

function onPointerMove(event) {
  pointer.value = { x: event.clientX, y: event.clientY };
}

function onClick(event) {
  event.preventDefault();
  event.stopPropagation();
  const point = { x: event.clientX, y: event.clientY };
  if (!anchor.value) {
    anchor.value = point;
    pointer.value = point;
    return;
  }
  const viewport = rectFrom(anchor.value, point);
  // A stray double-click would otherwise "select" nothing and hand the capture
  // a zero-area crop, which reads as a broken screenshot rather than a misclick.
  if (viewport.width < 8 || viewport.height < 8) {
    anchor.value = point;
    pointer.value = point;
    return;
  }
  const topLeft = toScreen(viewport.x, viewport.y);
  finish({
    viewport,
    screen: { ...topLeft, width: viewport.width, height: viewport.height },
    devicePixelRatio: window.devicePixelRatio || 1,
    viewportSize: { width: window.innerWidth, height: window.innerHeight },
    windowSize: { width: window.outerWidth, height: window.outerHeight },
    screenSize: { width: window.screen?.width || 0, height: window.screen?.height || 0 },
  });
}

function onKeyDown(event) {
  if (event.key === 'Escape') {
    event.preventDefault();
    finish(null);
  }
}

// Capture phase on `window`: the app's own handlers sit on the elements, so
// this is the only place to intercept a click before the page acts on it.
const LISTENERS = [
  ['pointermove', onPointerMove],
  ['click', onClick],
  ['keydown', onKeyDown],
];

function teardown() {
  LISTENERS.forEach(([type, handler]) => window.removeEventListener(type, handler, true));
  active.value = false;
  anchor.value = null;
  pointer.value = null;
}

function finish(region) {
  const resolve = resolveRegion;
  resolveRegion = null;
  teardown();
  resolve?.(region || null);
}

/** Start drawing. Resolves with the region, or null when cancelled. */
function start() {
  if (active.value) return Promise.resolve(null);
  active.value = true;
  anchor.value = null;
  pointer.value = null;
  LISTENERS.forEach(([type, handler]) => window.addEventListener(type, handler, true));
  return new Promise((resolve) => {
    resolveRegion = resolve;
  });
}

export function useRegionSelector() {
  return { active, anchor, pointer, currentRect, start, cancel: () => finish(null) };
}
